import { bus } from "../core/bus";

const HIT_FLASH_DURATION = 0.12;
const KILL_FLASH_DURATION = 0.55;

export type IFFState = "neutral" | "friendly" | "enemy";
export type ReticleWeapon = "chaingun" | "autocannon" | "howitzer" | "spotter";

export class ReticleHud {
  private readonly el: HTMLElement;
  private readonly killEl: HTMLElement;
  private tagEl!: HTMLElement;
  private flashLife = 0;
  private killLife = 0;
  private iff: IFFState = "neutral";
  private readonly onEnemyKilled = (): void => {
    this.killLife = KILL_FLASH_DURATION;
  };

  constructor() {
    const el = document.querySelector(".hud-reticle") as HTMLElement | null;
    if (!el) throw new Error("Missing .hud-reticle element");
    this.el = el;

    this.killEl = document.createElement("div");
    this.killEl.style.cssText = [
      "position:absolute",
      "inset:0",
      "display:flex",
      "align-items:center",
      "justify-content:center",
      "font-size:22px",
      "font-family:monospace",
      "font-weight:bold",
      "color:rgba(255,80,60,0)",
      "pointer-events:none",
      "user-select:none",
    ].join(";");
    this.killEl.textContent = "✕";
    this.el.style.position = "relative";
    // Sensor-footage reticle (see docs/DEVLOG.md sprint 20 research): a
    // segmented cross with a center gap — the aim point is never covered —
    // plus per-station furniture (stadia ticks, blast ring, FOV brackets).
    this.el.insertAdjacentHTML("afterbegin", `
      <span class="ret-arm ret-arm--n"></span>
      <span class="ret-arm ret-arm--s"></span>
      <span class="ret-arm ret-arm--e"></span>
      <span class="ret-arm ret-arm--w"></span>
      <span class="ret-dot"></span>
      <span class="ret-ring"></span>
      <span class="ret-bracket ret-bracket--tl"></span>
      <span class="ret-bracket ret-bracket--tr"></span>
      <span class="ret-bracket ret-bracket--bl"></span>
      <span class="ret-bracket ret-bracket--br"></span>
      <span class="ret-tag"></span>
    `);
    this.tagEl = this.el.querySelector(".ret-tag") as HTMLElement;
    this.el.appendChild(this.killEl);
    this.setWeapon("chaingun");

    bus.on("enemy:killed", this.onEnemyKilled);
  }

  flash(): void {
    this.flashLife = HIT_FLASH_DURATION;
  }

  setIFF(state: IFFState): void {
    this.iff = state;
  }

  setWeapon(weapon: ReticleWeapon): void {
    this.el.className = `hud-reticle hud-reticle--${weapon}`;
    this.tagEl.textContent = weapon === "spotter" ? "SPT — EYES ONLY" : "";
  }

  update(dt: number): void {
    if (this.killLife > 0) {
      this.killLife -= dt;
      const t = Math.max(0, this.killLife / KILL_FLASH_DURATION);
      this.killEl.style.color = `rgba(255,80,60,${t.toFixed(2)})`;
    } else {
      this.killEl.style.color = "rgba(255,80,60,0)";
    }

    if (this.flashLife > 0) {
      this.flashLife -= dt;
      const a = this.flashLife > 0 ? this.flashLife / HIT_FLASH_DURATION : 0;
      this.el.style.borderColor = `rgba(255,255,255,${(0.7 + 0.3 * a).toFixed(2)})`;
      this.el.style.color = `rgba(255,255,255,${(0.7 + 0.3 * a).toFixed(2)})`;
      return;
    }

    switch (this.iff) {
      case "friendly":
        this.el.style.borderColor = "rgba(80,255,120,0.9)";
        this.el.style.color = "rgba(80,255,120,0.9)";
        break;
      case "enemy":
        this.el.style.borderColor = "rgba(255,80,80,0.9)";
        this.el.style.color = "rgba(255,80,80,0.9)";
        break;
      default:
        this.el.style.borderColor = "rgba(200,255,220,0.7)";
        this.el.style.color = "rgba(200,255,220,0.7)";
    }
  }

  dispose(): void {
    bus.off("enemy:killed", this.onEnemyKilled);
    this.killEl.remove();
  }
}
